/* eslint-disable react-refresh/only-export-components */
import { useAccessContext } from "./useAccessContext";
import { HOURS_MANAGEMENT_ROLES, PLATFORM_ROLES, STAFF_APP_ROLES, type AppRole } from "./accessRoleUtils";

export type { AppRole } from "./accessRoleUtils";
export { HOURS_MANAGEMENT_ROLES, PLATFORM_ROLES, STAFF_APP_ROLES } from "./accessRoleUtils";

export function useUserRoles() {
  const {
    data: accessContext,
    isLoading: accessLoading,
    isFetching,
    rolesUnavailable,
    refetch: refetchAccessContext,
  } = useAccessContext();

  const roles = accessContext?.app_roles ?? [];
  // Same, but without JWT-metadata-trusted grants folded in — use this for a
  // gate whose matching RLS policy queries user_roles directly (see
  // AccessContext.app_roles_db_only for why that distinction matters). This
  // is the global counterpart of orgRolesDbOnly below, for a role check that
  // (like a bare has_any_role() call with no organization_id filter) isn't
  // org-scoped in the first place.
  const rolesDbOnly = accessContext?.app_roles_db_only ?? [];
  // Roles scoped to the ACTIVE org (+ global null-org grants). Use these for
  // org-scoped gates so a role held only in another org doesn't grant access here.
  const orgRoles = accessContext?.org_app_roles ?? [];
  // Same, but without JWT-metadata-trusted grants folded in — use this for a
  // gate whose matching RLS policy queries user_roles directly (see
  // AccessContext.org_app_roles_db_only for why that distinction matters).
  const orgRolesDbOnly = accessContext?.org_app_roles_db_only ?? [];
  // True only when public.organization_members holds a row for (this user, the active org) — `org_role` comes straight from
  // that query (useAccessContext) and is null whenever it does not. A role gate that skips this can pass for a user who holds
  // a role (even a DB-backed one, org-scoped or global) but was never added to the active org: `can_access_finance_org` and
  // every RLS policy built on it require BOTH a role AND membership, so such a user would be shown a "could not check" that
  // can never succeed (Codex P2, PR #6628, round 11).
  const isOrgMember = Boolean(accessContext?.org_role);
  // That organization_members.role itself, for a gate whose RLS policy reads it (the Stripe feed reads, for one).
  const orgRole: string | null = accessContext?.org_role ?? null;

  const hasRole = (role: AppRole) => roles.includes(role);
  const hasAnyRole = (checkRoles: AppRole[]) =>
    checkRoles.some((role) => roles.includes(role));
  const hasAnyRoleDbOnly = (checkRoles: AppRole[]) =>
    checkRoles.some((role) => rolesDbOnly.includes(role));
  const hasAnyOrgRole = (checkRoles: AppRole[]) =>
    checkRoles.some((role) => orgRoles.includes(role));
  const hasAnyOrgRoleDbOnly = (checkRoles: AppRole[]) =>
    checkRoles.some((role) => orgRolesDbOnly.includes(role));

  const isStaff = Boolean(
    accessContext?.is_staff ||
      accessContext?.capabilities.isOrgAdmin ||
      hasAnyRole(STAFF_APP_ROLES),
  );
  const isLoading = accessLoading;

  const hasPlatformRole = hasAnyRole([...PLATFORM_ROLES, "super_admin"]);
  // has_any_role() -- the Postgres function most RLS policies call for a
  // bare, non-org-scoped platform-role check -- reads only public.user_roles,
  // so a metadata-only super_admin/platform_support/etc. grant with no DB row
  // passes hasPlatformRole but is rejected there. Use this (not
  // hasPlatformRole) for a gate whose matching RLS check is that kind of bare
  // has_any_role() call (Codex P2, PR #6406, round 12).
  const hasPlatformRoleDbOnly = hasAnyRoleDbOnly([...PLATFORM_ROLES, "super_admin"]);
  const isPlatformSuperAdmin = hasAnyRole(["platform_super_admin", "super_admin"]);
  const isPlatformSupport = hasRole("platform_support");
  const isPlatformReadonly = hasRole("platform_readonly");

  return {
    roles,
    rolesDbOnly,
    orgRoles,
    orgRolesDbOnly,
    isOrgMember,
    orgRole,
    isLoading,
    isFetching,
    // The roles failed to load (timeout, 5xx, a swallowed 401/403), so the empty roles above are
    // fallbacks, not proof of a denial: show a retryable error, never a no-permission state.
    rolesUnavailable: rolesUnavailable === true,
    retryRoles: () => { void refetchAccessContext(); },
    hasRole,
    hasAnyRole,
    hasAnyRoleDbOnly,
    hasAnyOrgRole,
    hasAnyOrgRoleDbOnly,
    isStaff,
    hasPlatformRole,
    hasPlatformRoleDbOnly,
    isPlatformAdmin: hasPlatformRole,
    isPlatformSuperAdmin,
    isPlatformSupport,
    isPlatformReadonly,
    isAdmin: hasRole("admin"),
    isFinance: hasAnyRole(["admin", "finance"]),
    // Church-giving / general finance staff (org-scoped): may VIEW the giving
    // surfaces (contributions, pledges) and finance reports. Broader than
    // `isFinance` so finance_admin/treasurer/bookkeeper/auditor aren't hard-blocked
    // when drilling into records. Excludes ap_clerk/ar_clerk/approver — their domain
    // is the AP/AR/approval accounting subsystem, not church giving (and they have no
    // server read on contributions/pledges). Use for VIEW gates only; per-action
    // writes are gated by the permission matrix (`can(...)`) + server RLS. Org-scoped
    // so a finance role held only in another org doesn't grant access here.
    isFinanceStaff: hasAnyOrgRole([
      "admin",
      "finance",
      "finance_admin",
      "treasurer",
      "bookkeeper",
      "auditor",
    ]),
    isPastor: hasAnyRole(["admin", "pastor"]),
    isAuditor: hasAnyRole(["admin", "auditor"]),
    isMinistryLeader: hasRole("ministry_leader"),
    isSuperAdmin: isPlatformSuperAdmin,
  };
}